/**
 * Country key to ISO 3166-1 alpha-2 resolution.
 * Supports composite keys (e.g. belgium_fr, belgium_nl) and common aliases.
 * For Media Devoted | Created By Ciro Autiero
 */

const COUNTRY_ISO2_DATA = `
AD|Andorra
AE|United Arab Emirates
AF|Afghanistan
AG|Antigua and Barbuda
AI|Anguilla
AL|Albania
AM|Armenia
AO|Angola
AQ|Antarctica
AR|Argentina
AS|American Samoa
AT|Austria
AU|Australia
AW|Aruba
AX|Åland Islands
AZ|Azerbaijan
BA|Bosnia and Herzegovina
BB|Barbados
BD|Bangladesh
BE|Belgium
BF|Burkina Faso
BG|Bulgaria
BH|Bahrain
BI|Burundi
BJ|Benin
BL|Saint Barthélemy
BM|Bermuda
BN|Brunei
BO|Bolivia
BQ|Bonaire, Sint Eustatius and Saba
BR|Brazil
BS|Bahamas
BT|Bhutan
BV|Bouvet Island
BW|Botswana
BY|Belarus
BZ|Belize
CA|Canada
CC|Cocos (Keeling) Islands
CD|Congo (DRC)
CF|Central African Republic
CG|Congo
CH|Switzerland
CI|Ivory Coast
CK|Cook Islands
CL|Chile
CM|Cameroon
CN|China
CO|Colombia
CR|Costa Rica
CU|Cuba
CV|Cabo Verde
CW|Curaçao
CX|Christmas Island
CY|Cyprus
CZ|Czech Republic
DE|Germany
DJ|Djibouti
DK|Denmark
DM|Dominica
DO|Dominican Republic
DZ|Algeria
EC|Ecuador
EE|Estonia
EG|Egypt
EH|Western Sahara
ER|Eritrea
ES|Spain
ET|Ethiopia
FI|Finland
FJ|Fiji
FK|Falkland Islands
FM|Federated States of Micronesia
FO|Faroe Islands
FR|France
GA|Gabon
GB|United Kingdom
GD|Grenada
GE|Georgia
GF|French Guiana
GG|Guernsey
GH|Ghana
GI|Gibraltar
GL|Greenland
GM|Gambia
GN|Guinea
GP|Guadeloupe
GQ|Equatorial Guinea
GR|Greece
GS|South Georgia and the South Sandwich Islands
GT|Guatemala
GU|Guam
GW|Guinea-Bissau
GY|Guyana
HK|Hong Kong
HM|Heard Island and McDonald Islands
HN|Honduras
HR|Croatia
HT|Haiti
HU|Hungary
ID|Indonesia
IE|Ireland
IL|Israel
IM|Isle of Man
IN|India
IO|British Indian Ocean Territory
IQ|Iraq
IR|Iran
IS|Iceland
IT|Italy
JE|Jersey
JM|Jamaica
JO|Jordan
JP|Japan
KE|Kenya
KG|Kyrgyzstan
KH|Cambodia
KI|Kiribati
KM|Comoros
KN|Saint Kitts and Nevis
KP|North Korea
KR|South Korea
KW|Kuwait
KY|Cayman Islands
KZ|Kazakhstan
LA|Laos
LB|Lebanon
LC|Saint Lucia
LI|Liechtenstein
LK|Sri Lanka
LR|Liberia
LS|Lesotho
LT|Lithuania
LU|Luxembourg
LV|Latvia
LY|Libya
MA|Morocco
MC|Monaco
MD|Moldova
ME|Montenegro
MF|Saint Martin
MG|Madagascar
MH|Marshall Islands
MK|North Macedonia
ML|Mali
MM|Myanmar
MN|Mongolia
MO|Macao
MP|Northern Mariana Islands
MQ|Martinique
MR|Mauritania
MS|Montserrat
MT|Malta
MU|Mauritius
MV|Maldives
MW|Malawi
MX|Mexico
MY|Malaysia
MZ|Mozambique
NA|Namibia
NC|New Caledonia
NE|Niger
NF|Norfolk Island
NG|Nigeria
NI|Nicaragua
NL|Netherlands
NO|Norway
NP|Nepal
NR|Nauru
NU|Niue
NZ|New Zealand
OM|Oman
PA|Panama
PE|Peru
PF|French Polynesia
PG|Papua New Guinea
PH|Philippines
PK|Pakistan
PL|Poland
PM|Saint Pierre and Miquelon
PN|Pitcairn Islands
PR|Puerto Rico
PS|Palestine
PT|Portugal
PW|Palau
PY|Paraguay
QA|Qatar
RE|Réunion
RO|Romania
RS|Serbia
RU|Russia
RW|Rwanda
SA|Saudi Arabia
SB|Solomon Islands
SC|Seychelles
SD|Sudan
SE|Sweden
SG|Singapore
SH|St. Helena
SI|Slovenia
SJ|Svalbard and Jan Mayen
SK|Slovakia
SL|Sierra Leone
SM|San Marino
SN|Senegal
SO|Somalia
SR|Suriname
SS|South Sudan
ST|Sao Tome and Principe
SV|El Salvador
SX|Sint Maarten
SY|Syria
SZ|Eswatini
TC|Turks and Caicos Islands
TD|Chad
TF|French Southern Territories
TG|Togo
TH|Thailand
TJ|Tajikistan
TK|Tokelau
TL|Timor-Leste
TM|Turkmenistan
TN|Tunisia
TO|Tonga
TR|Turkey
TT|Trinidad and Tobago
TV|Tuvalu
TW|Taiwan
TZ|Tanzania
UA|Ukraine
UG|Uganda
UM|United States Minor Outlying Islands
US|United States
UY|Uruguay
UZ|Uzbekistan
VA|Vatican City
VC|Saint Vincent and the Grenadines
VE|Venezuela
VG|British Virgin Islands
VI|U.S. Virgin Islands
VN|Vietnam
VU|Vanuatu
WF|Wallis and Futuna
WS|Samoa
YE|Yemen
YT|Mayotte
ZA|South Africa
ZM|Zambia
ZW|Zimbabwe
`.trim();

const ALIASES: Record<string, string> = {
  uae: "AE",
  uk: "GB",
  "united kingdom": "GB",
  "new zealand": "NZ",
  newzealand: "NZ",
  "great britain": "GB",
  england: "GB",
  scotland: "GB",
  wales: "GB",
  "northern ireland": "GB",
  usa: "US",
  "united states": "US",
  "united states of america": "US",
  russia: "RU",
  "south korea": "KR",
  "north korea": "KP",
  bolivia: "BO",
  venezuela: "VE",
  iran: "IR",
  syria: "SY",
  vietnam: "VN",
  laos: "LA",
  myanmar: "MM",
  burma: "MM",
  "czech republic": "CZ",
  czech: "CZ",
  // The ISO 3166 short name, and what the banks stored before the rename to
  // "Czech Republic" (the wording Voluum/Binom use) — keep resolving it.
  czechia: "CZ",
  turkey: "TR",
  // ISO 3166's official name, and what the banks stored before the rename to
  // "Turkey". normalizeCountryKey strips the diacritic, so this covers
  // "Türkiye" too.
  turkiye: "TR",
  "cape verde": "CV",
  "ivory coast": "CI",
  palestine: "PS",
  macedonia: "MK",
  taiwan: "TW",
  "hong kong": "HK",
  macau: "MO",
  moldova: "MD",
  swaziland: "SZ",
  // Pre-rename ISO 3166 official names. The tables now carry the common
  // English name the trackers use; these keep older stored values and raw
  // tracker payloads resolving to the same country.
  "brunei darussalam": "BN",
  "bolivia plurinational state of": "BO",
  "congo the democratic republic of the": "CD",
  "cote d ivoire": "CI",
  "falkland islands malvinas": "FK",
  "micronesia federated states of": "FM",
  "iran islamic republic of": "IR",
  "korea democratic people s republic of": "KP",
  "korea republic of": "KR",
  "lao people s democratic republic": "LA",
  "moldova republic of": "MD",
  "saint martin french part": "MF",
  pitcairn: "PN",
  "palestine state of": "PS",
  "russian federation": "RU",
  "saint helena ascension and tristan da cunha": "SH",
  "sint maarten dutch part": "SX",
  "syrian arab republic": "SY",
  "taiwan province of china": "TW",
  "tanzania united republic of": "TZ",
  // "united states of america" is already aliased above.
  "holy see": "VA",
  "venezuela bolivarian republic of": "VE",
  "virgin islands british": "VG",
  "virgin islands u s": "VI",
  "viet nam": "VN",
};

function normalizeCountryKey(input: string): string {
  return (input || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/&/g, "and")
    .replace(/[()'",.]/g, " ")
    .replace(/\s+/g, " ")
    .replace(/-/g, "_")
    .trim();
}

const NAME_TO_ISO2 = (() => {
  const map = new Map<string, string>();
  for (const line of COUNTRY_ISO2_DATA.split("\n")) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    const [iso2Raw, nameRaw] = trimmed.split("|");
    const iso2 = (iso2Raw || "").trim().toUpperCase();
    const name = (nameRaw || "").trim();
    if (!/^[A-Z]{2}$/.test(iso2) || !name) continue;
    map.set(normalizeCountryKey(name), iso2);
  }
  for (const [alias, iso2] of Object.entries(ALIASES)) {
    map.set(normalizeCountryKey(alias), iso2.toUpperCase());
  }
  return map;
})();

const ISO2_TO_NAME = (() => {
  const map = new Map<string, string>();
  for (const line of COUNTRY_ISO2_DATA.split("\n")) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    const [iso2Raw, nameRaw] = trimmed.split("|");
    const iso2 = (iso2Raw || "").trim().toUpperCase();
    const name = (nameRaw || "").trim();
    if (!/^[A-Z]{2}$/.test(iso2) || !name) continue;
    if (!map.has(iso2)) map.set(iso2, name);
  }
  return map;
})();

/**
 * Maps an ISO 3166-1 alpha-2 code to its canonical display name — the exact
 * spelling used by the country dropdowns (COUNTRY_NAMES). Returns null when the
 * code is unknown. Inverse of {@link countryKeyToIso2}.
 */
export function iso2ToCountryName(
  iso2: string | null | undefined,
): string | null {
  const code = (iso2 || "").trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return null;
  return ISO2_TO_NAME.get(code) ?? null;
}

/**
 * For composite keys like belgium_fr, belgium_nl, returns the base part (belgium).
 */
export function extractBaseCountryKey(input: string): string {
  const trimmed = (input || "").trim().toLowerCase().replace(/-/g, "_");
  if (!trimmed) return "";
  const idx = trimmed.indexOf("_");
  return idx >= 0 ? trimmed.slice(0, idx) : trimmed;
}

/**
 * Maps countryKey to ISO 3166-1 alpha-2. Returns null when unknown.
 * Supports composite keys (belgium_fr -> BE) and aliases (uk -> GB).
 */
export function countryKeyToIso2(
  countryKey: string | null | undefined,
): string | null {
  const raw = (countryKey || "").trim();
  if (!raw) return null;

  const normalized = raw.toLowerCase().replace(/-/g, "_");
  if (normalized.length === 2) {
    const aliased = ALIASES[normalized];
    return (aliased ?? raw.toUpperCase()) as string;
  }

  const key = normalizeCountryKey(raw);
  let iso2: string | null = NAME_TO_ISO2.get(key) ?? null;
  if (!iso2 && key.includes("_")) {
    const baseNorm = normalizeCountryKey(extractBaseCountryKey(raw));
    iso2 = NAME_TO_ISO2.get(baseNorm) ?? null;
  }
  if (!iso2 && key.includes("_")) {
    const withSpaces = key.replace(/_/g, " ");
    iso2 = NAME_TO_ISO2.get(withSpaces) ?? null;
  }
  return iso2;
}
